import { Component, OnInit, Input, OnDestroy, Output, EventEmitter } from '@angular/core';
import { ApiService } from 'src/app/services/api.service';
import { UntypedFormBuilder, UntypedFormGroup, Validators, UntypedFormControl } from '@angular/forms';
import { FormService } from 'src/app/services/form.service';
import { ToastMessageService } from 'src/app/widgets/toast-message/toast-message.service';
import { Subscription, forkJoin } from 'rxjs';
import { JournalEntryService } from '../../journal-entry.service';
import { JournalEntry } from '../../journal-entry.interface';
import { AuthService } from 'src/app/services/auth.service';
import { UsersService } from 'src/app/configuration/users/users.service';

@Component({
  selector: 'app-journal-entry-detail-form',
  templateUrl: './journal-entry-detail-form.component.html',
  styleUrls: ['./journal-entry-detail-form.component.scss']
})
export class JournalEntryDetailFormComponent implements OnInit, OnDestroy {

  @Input() id: number;

  @Output() saved: EventEmitter<any> = new EventEmitter();

  // states
  formProcessing = false;
  isSubmitted = false;
  loading = true;

  // menu options data
  companies: any[];

  // error messages
  formErrors: any = null;
  fieldErrors: any = {};

  // endpoints
  private _companyEp = `${this._apiSvc.endpoints.monolith}/api/Company`;
  private _journalEntryEp = `${this._apiSvc.endpoints.monolith}/api/GLJournalHead`;

  // subscriptions
  private _forkJoinSub: Subscription;
  private _journalEntrySub: Subscription;

  // form group
  form: UntypedFormGroup = this._fb.group({
    Id: [null],
    CompanyId: [null, Validators.required],
    BatchCode: [null],
    BatchNotes: [null],
    PostDtm: [null, Validators.required],
  });

  constructor(
    private _apiSvc: ApiService,
    private _authSvc: AuthService,
    private _fb: UntypedFormBuilder,
    private _formSvc: FormService,
    private _journalEntrySvc: JournalEntryService,
    private _toastMsgSvc: ToastMessageService,
    private _userSvc: UsersService
  ) { }

  ngOnInit() {

    this._getMenuData();

    if (this.id) {

      this.form.addControl( 'JournalDocument', new UntypedFormControl(null) );
      this.form.addControl( 'GLFiscalYearId', new UntypedFormControl(null) );

      this._journalEntrySub = this._journalEntrySvc.JournalEntry.subscribe((res: JournalEntry) => {
        if (res) {
          this._patchForm(res);
          this.loading = false;
          if (res.IsPosted || res.SystemProgramId !== 81) {
            this.form.disable();
          }
        }
      });

      if (!this._authSvc.hasClaim('GLJournalEntry', 'Edit')) {
        this.form.disable();
      }

    }

  }

  ngOnDestroy() {
    this._forkJoinSub.unsubscribe();
    if (this._journalEntrySub) {
      this._journalEntrySub.unsubscribe();
    }
  }

  /**
   * Patch form values
   */
  private _patchForm(record: any): void {
    const patchValue = this._formSvc.getPatchValue(this.form.controls, record);
    this.form.patchValue(patchValue);
  }

  /**
   * Get menu options data
   */
  private _getMenuData(): void {

    const obs: any = {
      companies: this._apiSvc.search(this._companyEp, {sort: 'CompanyDescription-asc'}, 'companies'),
      userDefaults: this._userSvc.getDefaults()
    };

    this._forkJoinSub = forkJoin(obs).subscribe((res: any) => {

      if (obs.companies) {

        this.companies = res.companies.Data;
        if (this.companies.length === 1 && !this.id) {
          this.form.get('CompanyId').setValue( this.companies[0]['Id'] );
        }

      }

      if (res.userDefaults && res.userDefaults.CompanyId) {
        this.form.get('CompanyId').setValue(res.userDefaults.CompanyId);
      }

      this.loading = false;

    });

  }

  /**
   * Form submission event handler
   * @param e
   */
  onSubmit(e: Event): void {

    e.preventDefault();

    this.isSubmitted = true;

    if (!this.form.valid || this.formProcessing) {
      return;
    }

    // clear errors
    this.formErrors = null;
    this.fieldErrors = {};

    this.formProcessing = true;

    const action = this.id ? 'update' : 'add',
      endpoint = this.id ? `${this._journalEntryEp}/${this.id}` : this._journalEntryEp;

    this._apiSvc[action](endpoint, this.form.value)
      .subscribe((res: any) => {

        this._toastMsgSvc.send({
          type: 'success',
          message: 'Journal entry saved',
          icon: 'check_circle_outline'
        });

        this.formProcessing = false;

        this.saved.emit({confirm: true, data: res});

      }, (err: any) => {

        const errors = this._formSvc.processFormErrors(err);

        if (errors.formErrors) {
          this.formErrors = errors.formErrors;
        }

        if (errors.fieldErrors) {
          this.fieldErrors = errors.fieldErrors;
        }

        this.formProcessing = false;

      });

  }

}
